"use client";

import type { FormEvent, ReactNode } from "react";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Khung chung cho mọi modal tự dựng trong app.
 *
 * Lý do tồn tại: các modal cũ dùng `fixed inset-0 ... flex items-center` mà không
 * cho overlay cuộn. Panel canh giữa mà cao hơn viewport thì tràn ra CẢ trên lẫn
 * dưới, và vì overlay là `position: fixed` nên cuộn trang phía sau không kéo nó
 * lên được — hàng nút submit nằm ngoài màn hình và KHÔNG THỂ bấm tới. Đo trên
 * stage ở 740×400: modal "Thêm người dùng" có top = -106px, nút "Tạo người dùng"
 * ở y = 470px trong viewport cao 400px.
 *
 * Khung này chặn lỗi đó bằng ba việc, cả ba đều bắt buộc:
 *   1. overlay `overflow-y-auto` + canh trên (`p-4`, không `items-center`);
 *   2. panel `max-h-[90vh]` + `flex-col` để header/footer cố định;
 *   3. thân `min-h-0 flex-1 overflow-y-auto` — thiếu `min-h-0` thì flex item
 *      không co lại được và `overflow-y-auto` mất tác dụng.
 */

const SIZE_CLASSES = {
	md: "max-w-md",
	lg: "max-w-lg",
	"2xl": "max-w-2xl",
	"3xl": "max-w-3xl",
	"4xl": "max-w-4xl",
} as const;

export type ModalSize = keyof typeof SIZE_CLASSES;

interface ModalShellProps {
	isOpen: boolean;
	onClose: () => void;
	title: ReactNode;
	/** Icon tròn bên trái tiêu đề; bỏ trống thì header chỉ có chữ. */
	icon?: ReactNode;
	size?: ModalSize;
	/**
	 * `icon` = nút X ghost (mặc định), `labeled` = nút "Đóng" có chữ,
	 * `none` = không có nút đóng trên header (modal tự lo ở footer).
	 */
	closeButton?: "icon" | "labeled" | "none";
	/** Bấm ra nền ngoài để đóng. Tắt mặc định: form đang nhập dở không nên mất khi lỡ tay. */
	closeOnBackdrop?: boolean;
	/** Khi có, thân + footer được bọc trong `<form>` nên nút `type="submit"` ở footer chạy đúng. */
	onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
	/** Hàng nút cố định đáy modal, không cuộn theo thân. */
	footer?: ReactNode;
	children: ReactNode;
}

export function ModalShell({
	isOpen,
	onClose,
	title,
	icon,
	size = "md",
	closeButton = "icon",
	closeOnBackdrop = false,
	onSubmit,
	footer,
	children,
}: ModalShellProps) {
	if (!isOpen) return null;

	// Chỉ đóng khi bấm đúng vào nền, không phải khi sự kiện nổi bọt lên từ panel.
	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (closeOnBackdrop && e.target === e.currentTarget) onClose();
	};

	const body = (
		<>
			<div className="min-h-0 flex-1 overflow-y-auto p-6">{children}</div>
			{footer && <div className="shrink-0 border-t border-border p-6">{footer}</div>}
		</>
	);

	return (
		<div
			className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 p-4 sm:p-8"
			onClick={handleBackdropClick}
		>
			<div
				className={cn(
					// Trừ đúng padding của overlay, không phải `90vh`: `90vh` cộng thêm
					// padding sẽ vượt quá chiều cao màn hình khi viewport thấp, và panel
					// thò ra ngoài đúng bằng phần padding đó.
					"mx-auto flex max-h-[calc(100vh-2rem)] w-full flex-col rounded-lg bg-card shadow-xl sm:max-h-[calc(100vh-4rem)]",
					SIZE_CLASSES[size],
				)}
			>
				<div className="flex shrink-0 items-center gap-3 border-b p-6">
					{icon}
					<h2 className="flex-1 text-xl font-semibold text-foreground">{title}</h2>

					{closeButton === "icon" && (
						<Button
							variant="ghost"
							size="sm"
							onClick={onClose}
							aria-label="Đóng"
							className="h-8 w-8 shrink-0 p-0"
						>
							<X className="h-4 w-4" />
						</Button>
					)}

					{closeButton === "labeled" && (
						<Button
							variant="outline"
							size="sm"
							onClick={onClose}
							className="flex shrink-0 items-center gap-2"
						>
							<X className="h-4 w-4" />
							Đóng
						</Button>
					)}
				</div>

				{onSubmit ? (
					<form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
						{body}
					</form>
				) : (
					<div className="flex min-h-0 flex-1 flex-col">{body}</div>
				)}
			</div>
		</div>
	);
}
